/**
 * Simple logger utility that respects environment
 * 
 * In production: Only errors are logged
 * In development: All logs are shown
 * 
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.debug('Some debug info', data);  // Only in dev
 *   logger.info('Info message');            // Only in dev
 *   logger.warn('Warning');                 // Only in dev
 *   logger.error('Error', error);           // Always logged
 */

// Check if we're in development mode
// In production builds, NODE_ENV will be 'production'
const isDev = process.env.NODE_ENV !== 'production';

// Categories that can be individually enabled/disabled
type LogCategory = 'websocket' | 'audio' | 'session' | 'analysis' | 'hints' | 'general';

// In development, all categories are enabled by default
const isEnabled = (_category: LogCategory): boolean => {
  // In dev, allow all categories; in prod, disable all
  return isDev;
};

export const logger = {
  /**
   * Debug-level logging - only in development
   * Use for detailed debugging info
   */
  debug: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },

  /**
   * Info-level logging - only in development
   * Use for general information
   */
  info: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.log(message, ...args);
    }
  },

  /**
   * Warning-level logging - only in development
   * Use for potential issues
   */
  warn: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.warn(message, ...args);
    }
  },

  /**
   * Error-level logging - ALWAYS logged
   * Use for actual errors
   */
  error: (message: string, ...args: unknown[]): void => {
    console.error(message, ...args);
  },

  /**
   * Category-specific logging
   * Can be filtered by category even in development
   */
  category: (cat: LogCategory, message: string, ...args: unknown[]): void => {
    if (isEnabled(cat)) {
      console.log(`[${cat.toUpperCase()}] ${message}`, ...args);
    }
  },
};

// Shortcuts for common categories
export const wsLog = (message: string, ...args: unknown[]) => 
  logger.category('websocket', message, ...args);

export const audioLog = (message: string, ...args: unknown[]) => 
  logger.category('audio', message, ...args);

export const sessionLog = (message: string, ...args: unknown[]) => 
  logger.category('session', message, ...args);

export default logger;
